import React, { useEffect, useState } from 'react';
import './board-stats.scss';
import { BoardStatKeys, BoardStats, contractSuits, suitsFullString } from '../../_models/models';
import { IRootState } from '../../store/_root.reducer';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { suitArray } from '../../../game-engine-interface/game-engine-helper';
import { renderStatRowExternal } from './board-stat-row-render';

export interface InstaBoardStatsProps extends StateProps, DispatchProps {
    my_stat?: BoardStatKeys;
    stats?: BoardStats[];
    selectedKey?: BoardStatKeys;
    isOpen?: boolean;
    onSelect?: (key: BoardStatKeys, stat: BoardStats) => void;
    onToggleOpen?: () => void;
}

const InstaBoardStats: React.FC<InstaBoardStatsProps> = ({ my_stat, stats, urlParams, selectedKey, isOpen, onSelect, onToggleOpen }) => {
    const { t } = useTranslation();

    // ---- internal fallback state if parent doesn't control everything ----
    const [internalOpen, setInternalOpen] = useState(true);
    const [internalSelectedKey, setInternalSelectedKey] = useState<BoardStatKeys | undefined>(selectedKey);

    useEffect(() => {
        if (selectedKey) {
            setInternalSelectedKey(selectedKey);
        }
    }, [selectedKey]);

    // ---- guard ----
    if (!stats) {
        return null;
    }

    const open = typeof isOpen === 'boolean' ? isOpen : internalOpen;
    const selected = selectedKey ?? internalSelectedKey;

    const handleToggle = () => {
        if (onToggleOpen) {
            onToggleOpen();
        } else {
            setInternalOpen(prev => !prev);
        }
    };
    const keyFields: (keyof BoardStatKeys)[] = ['r', 's', 'c', 'd', 'x', 'l'];
    const normalizeKey = (key?: BoardStatKeys) => keyFields.map(k => (key ? key[k] : '')).join('-');
    const keysEqual = (a?: BoardStatKeys, b?: BoardStatKeys) => normalizeKey(a) === normalizeKey(b);

    const handleRowClick = (_index: number, stat: BoardStats) => {
        const key = stat.key;
        if (onSelect) {
            onSelect(key, stat);
        } else {
            setInternalSelectedKey(key);
            setInternalOpen(false);
        }
    };

    const d = [
        t('boardStats.bridgePosition.n'),
        t('boardStats.bridgePosition.e'),
        t('boardStats.bridgePosition.s'),
        t('boardStats.bridgePosition.w')
    ];

    const columnTitles = [
        t('boardStats.declarer'),
        urlParams.objective ? t('boardStats.objective') : t('boardStats.contract'),
        t('boardStats.lead'),
        t('boardStats.tricks'),
        t('boardStats.score'),
        t('boardStats.result'),
        t('boardStats.players')
    ];

    const selectedStat = selected ? stats.find(s => keysEqual(s.key, selected)) : undefined;

    // Helper to compute "isMe" for any given row (used in both table and header)
    const isMeForRow = (_boardStats: BoardStats) => {
        const keyStats = keyFields
            .map(key => _boardStats.key[key])
            .sort()
            .join('-');
        const myStats = keyFields
            .map(key => (my_stat ? my_stat[key] : {}))
            .sort()
            .join('-');

        return keyStats === myStats || _boardStats.key.is_me;
    };

    return (
        <section className={['BoardStats', stats.length === 0 ? 'NoStats' : '', open ? 'open' : 'collapsed'].join(' ')}>
            {/* HEADER / DROPDOWN BUTTON – now contains the selection */}
            <button type="button" className="BoardStats__header" onClick={handleToggle}>
                <div className="BoardStats__header-content">
                    {open && <div className="BoardStats__header-title">{t('boardStats.travellerHeader', 'Select Sample Score')}</div>}
                    {!open && selectedStat && (
                        <div className="BoardStats__header-selection table table--preview">
                            {renderStatRowExternal(selectedStat, 0, {
                                isMe: isMeForRow(selectedStat),
                                isSelected: true,
                                disableClick: true,
                                dLabels: d
                            })}
                        </div>
                    )}
                </div>

                <span className="BoardStats__header-chevron">{open ? '▲' : '▼'}</span>
            </button>

            {/* FULL TRAVELLER TABLE WHEN OPEN */}
            {open && (
                <div className="BoardStats__panel">
                    <div className="table">
                        <div>
                            {columnTitles.map(title => (
                                <div key={title}>{title}</div>
                            ))}
                        </div>
                        {stats.map((_boardStats, index) =>
                            renderStatRowExternal(_boardStats, index, {
                                isMe: isMeForRow(_boardStats),
                                isSelected: selected ? keysEqual(_boardStats.key, selected) : false,
                                onClick: handleRowClick,
                                dLabels: d
                            })
                        )}
                    </div>
                </div>
            )}
        </section>
    );
};

const mapStateToProps = ({ game, app }: IRootState) => ({
    urlParams: app.urlParams
});

const mapDispatchToProps = {};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(InstaBoardStats);
